import { Buffer } from 'buffer'
import { address as bitcoinAddress } from 'bitcoinjs-lib'
import Wallet, { ensureEccLib } from './wallet'
import { signOwnedPsbts } from './market/secure-sign'
import { inputSighashTypes, parsePsbt } from './market/secure-psbt'
import { beginOfferSigning } from './wallet/offer-signing-guard'
import { psbtInputScript } from './offer-templates'
import { OfferError } from './offer-errors'

type OfferSignPlan = Parameters<typeof signOwnedPsbts>[0][number]

/** What the native signer would sign on sight: the wallet's scripts and its x-only key. */
export interface OwnSigningMaterial {
	scripts: Buffer[]
	xonly: string | null
}

function walletSigningMaterial(): OwnSigningMaterial {
	const scripts: Buffer[] = []
	for (const candidate of [Wallet.getTaprootAddress(), Wallet.balanceAddress()]) {
		if (!candidate) continue
		try {
			scripts.push(bitcoinAddress.toOutputScript(candidate))
		} catch {
			/* An address of another network cannot own a mainnet input. */
		}
	}
	const key = Wallet.taprootPaymentPublicKey() || Wallet.getPublicKey()
	const xonly =
		typeof key === 'string' && /^[0-9a-f]{64}$|^0[23][0-9a-f]{64}$/i.test(key)
			? key.slice(-64).toLowerCase()
			: null
	return { scripts, xonly }
}

/**
 * The native signer signs every input whose prevout is one of the wallet's
 * addresses and every leaf that names the wallet key, with no per-input
 * confirmation once the password is cached. An offer plan names the inputs
 * the buyer agreed to sign; any other input that would attract a signature
 * is a template asking for more than the offer. Refuse it before the signer
 * ever sees the template.
 */
export function assertPlanCoversOwnInputs(plan: OfferSignPlan, own: OwnSigningMaterial): void {
	const psbt = parsePsbt(plan.psbt)
	for (let index = 0; index < psbt.inputCount; index++) {
		if (plan.indexes.includes(index)) continue
		const script = psbtInputScript(psbt, index)
		const ownScript = !!script && own.scripts.some((candidate) => candidate.equals(script))
		const ownLeaf =
			!!own.xonly &&
			(psbt.data.inputs[index]?.tapLeafScript ?? []).some((leaf) =>
				Buffer.from(leaf.script).toString('hex').includes(own.xonly!)
			)
		if (ownScript || ownLeaf)
			throw new OfferError(
				'offer_template_oversigns',
				`The offers service asked your wallet to sign input ${index}, which is not part of this offer. Your wallet did not sign it.`
			)
	}
}

/** True when an input carries any signature material at all. */
const inputSigned = (input: ReturnType<typeof parsePsbt>['data']['inputs'][number] | undefined) =>
	!!input &&
	(!!input.tapKeySig?.length ||
		!!input.tapScriptSig?.length ||
		!!input.partialSig?.length ||
		!!input.finalScriptWitness?.length ||
		!!input.finalScriptSig?.length)

/** Offer-only adapter. Reuse the protected signer, opting native escrow inputs
 * into the offer protocol without broadening listing/purchase permissions. */
export async function signOfferPsbts(
	plans: OfferSignPlan[],
	signal?: AbortSignal
): Promise<string[]> {
	const release = beginOfferSigning()
	try {
		signal?.throwIfAborted()
		// External wallets receive an explicit index list and sign only that.
		if (Wallet.type() !== 'ordinalswallet') return await signOwnedPsbts(plans, signal)
		await ensureEccLib()
		const own = walletSigningMaterial()
		for (const plan of plans) assertPlanCoversOwnInputs(plan, own)
		const seed = await Wallet.seed()
		signal?.throwIfAborted()
		if (!seed.valid) throw new Error('Unlock your wallet to sign the offer.')
		const { signRawtx } = await import('./bitcoin')
		const signed: string[] = []
		for (const plan of plans) {
			signal?.throwIfAborted()
			if (!plan.tapscript) {
				// Funding: only the planned inputs, and only with ALL or DEFAULT,
				// so no signature it makes can be lifted into another transaction.
				const hex = await signRawtx(seed.seed, {
					psbt: plan.psbt,
					disableExtract: true,
					signIndexes: plan.indexes,
					allowedSighashes: [0, 1]
				})
				if (parsePsbt(hex).data.inputs.some((input, i) => !plan.indexes.includes(i) && inputSigned(input)))
					throw new OfferError('offer_template_oversigns')
				signed.push(hex)
				continue
			}
			if (inputSighashTypes(plan.psbt, plan.indexes).some(([type]) => type !== 1 && type !== 0x82))
				throw new Error('The offer requested an unsupported escrow signature.')
			const hex = await signRawtx(seed.seed, {
				psbt: plan.psbt,
				disableExtract: true,
				offerEscrowIndexes: plan.indexes,
				signIndexes: plan.indexes
			})
			const result = parsePsbt(hex)
			if (plan.indexes.some((i) => !result.data.inputs[i]?.tapScriptSig?.length))
				throw new Error('The wallet did not sign the offer escrow. Nothing was submitted.')
			// The template arrived unsigned; nothing outside the plan may have gained a signature.
			if (result.data.inputs.some((input, i) => !plan.indexes.includes(i) && inputSigned(input)))
				throw new OfferError('offer_template_oversigns')
			signed.push(hex)
		}
		return signed
	} finally {
		release()
	}
}
